import type { IntestacyRuleSet } from "./types.js";

// QLD intestacy ruleset — Succession Act 1981 (Qld), Part 3 + Schedule 2
// ("Distribution of residuary estate on intestacy").
//
// Distribution (cited — Sch 2 Pt 1, Intestate survived by spouse):
//   spouse, no issue          → spouse takes the WHOLE estate.
//   spouse + ONE child        → spouse: household chattels + $150,000 +
//                                ONE HALF of the residue; the child takes the
//                                other half.
//   spouse + MORE THAN ONE    → spouse: household chattels + $150,000 +
//     child                      ONE THIRD of the residue; the children share
//                                the other two-thirds equally.
//   no spouse → issue take all (Sch 2 Pt 2); else parents; else
//     siblings/nephews/nieces; else grandparents/aunts/uncles; else the Crown.
//
// IMPORTANT QLD-specific differences from NSW/VIC, encoded as data:
//   1. The "statutory legacy" is a FIXED $150,000 "prescribed amount" — NOT
//      CPI-indexed. Modelled as a single open-ended period.
//   2. The spouse's residue fraction depends on the NUMBER of children: 1/2 with
//      one child, 1/3 with more than one. (`partnerResidueShareOneChild`.)
//   3. QLD does NOT give the spouse the whole estate merely because all children
//      are shared — the chattels + $150,000 + residue-fraction split applies even
//      to a spouse's own children. (`partnerTakesAllWhenAllShared: false`.)
//
// Sources: Succession Act 1981 (Qld) Sch 2 (legislation.qld.gov.au / AustLII);
// cross-checked against Queensland succession-law plain-language summaries
// (gotocourt.com.au, armstronglegal.com.au), 2026-06-23.
export const qld: IntestacyRuleSet = {
  state: "QLD",
  act: "Succession Act 1981 (Qld)",
  distributionRef: "Pt 3, Sch 2 Pt 1",
  sources: {
    act: "https://www.legislation.qld.gov.au/view/whole/html/inforce/current/act-1981-069",
    schedule2:
      "https://www5.austlii.edu.au/au/legis/qld/consol_act/sa1981138/sch2.html",
    plainEnglish:
      "https://www.gotocourt.com.au/wills-and-estates/qld/intestacy-who-inherits",
  },
  sourcesVerifiedAt: "2026-06-23",
  statutoryLegacy: [
    {
      // Fixed $150,000 "prescribed amount" — not indexed; open-ended period.
      effectiveFrom: "1981-01-01",
      effectiveTo: null,
      amount: 150_000,
      source:
        "https://www5.austlii.edu.au/au/legis/qld/consol_act/sa1981138/sch2.html",
    },
  ],
  // Sch 2 Pt 1: spouse takes 1/3 of residue with MORE THAN ONE child (default)...
  partnerResidueShare: 1 / 3,
  // ...and 1/2 with exactly ONE child.
  partnerResidueShareOneChild: 0.5,
  // QLD applies the split even when all children are the spouse's own.
  partnerTakesAllWhenAllShared: false,
};
